// ── IP 관리 — 최근 활동(온라인 사용자) 섹션 컴포넌트 ─────────────────────────
import React from 'react';
import {
  cardStyle, tableStyle, thStyle, tdStyle, paginationWrapStyle,
  mobileCardListStyle, mobileCardStyle, mobileCardHeadStyle, miniBadgeStyle,
  mobileInfoGridStyle, mobileInfoItemStyle, mobileInfoKeyStyle, mobileCodeStyle,
  mobileActionGridStyle, primaryBtn, btn, doneBtn, ipCodeStyle,
} from './styles';
import { normalizeIp, formatIpWithAlias, getStatusText } from './utils';

/**
 * 최근 활동 온라인 사용자 섹션
 * Props:
 *   isMobile         — 모바일 여부
 *   pagedOnlineUsers — 현재 페이지에 표시할 온라인 사용자 목록
 *   onlineUsers      — 전체 온라인 사용자 목록 (건수 표시용)
 *   onlinePage       — 현재 페이지 번호
 *   onlineTotalPages — 전체 페이지 수
 *   activeKeySet     — 직원 전용 활성 연결 집합 ("userId|ip")
 *   activePublicIpSet — 공용 활성 연결 IP 집합
 *   aliasByIp        — ip → 별칭 Map
 *   busyKey          — 등록 중인 버튼 키
 *   allowShared      — 공용 등록 버튼 보이기(통합 출퇴근 관리만 — 회사 화면은 직원 등록만)
 *   onPageChange     — 페이지 변경 콜백 (page) => void
 *   onRegisterUser   — 직원/공용 등록 콜백 (user, asPublic) => void
 */
export function OnlineUsersSection({
  isMobile, pagedOnlineUsers, onlineUsers,
  onlinePage, onlineTotalPages,
  activeKeySet, activePublicIpSet, aliasByIp,
  busyKey, allowShared = false, onPageChange, onRegisterUser,
}) {
  return (
    <div style={{ ...cardStyle, marginBottom: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>최근 활동</div>

      {onlineUsers.length === 0 ? (
        <div style={{ color: 'var(--text-muted)' }}>최근 접속 기록이 없습니다.</div>
      ) : (
        <>
          {isMobile ? (
            /* 모바일 카드 */
            <div style={mobileCardListStyle}>
              {pagedOnlineUsers.map((user) => {
                const ip             = normalizeIp(user.ip_address);
                const userKey        = `${Number(user.user_id)}|${ip}`;
                const registeredUser = activeKeySet.has(userKey);
                const registeredPub  = activePublicIpSet.has(ip);
                const userBusy       = busyKey === `${Number(user.user_id)}|${ip}`;
                const publicBusy     = busyKey === `public|${ip}`;

                return (
                  <article key={`${user.user_id}-${user.last_seen_at}`} style={mobileCardStyle}>
                    <div style={mobileCardHeadStyle}>
                      <strong style={{ fontSize: 15 }}>{user.name}</strong>
                      <span style={miniBadgeStyle}>{getStatusText(registeredUser, registeredPub)}</span>
                    </div>
                    <div style={mobileInfoGridStyle}>
                      <div style={mobileInfoItemStyle}>
                        <span style={mobileInfoKeyStyle}>아이디</span>
                        <span>{user.login_id}</span>
                      </div>
                      <div style={mobileInfoItemStyle}>
                        <span style={mobileInfoKeyStyle}>부서</span>
                        <span>{user.department || '-'}</span>
                      </div>
                      <div style={{ ...mobileInfoItemStyle, gridColumn: '1 / -1' }}>
                        <span style={mobileInfoKeyStyle}>현재 IP</span>
                        <code style={mobileCodeStyle}>{formatIpWithAlias(ip, aliasByIp.get(ip))}</code>
                      </div>
                    </div>
                    <div style={allowShared ? mobileActionGridStyle : { ...mobileActionGridStyle, gridTemplateColumns: '1fr' }}>
                      <button style={registeredUser ? doneBtn : primaryBtn} disabled={registeredUser || userBusy}
                        onClick={() => onRegisterUser(user, false)}>
                        {registeredUser ? '직원 등록됨' : userBusy ? '등록 중...' : '직원으로 등록'}
                      </button>
                      {allowShared && (
                        <button style={registeredPub ? doneBtn : btn} disabled={registeredPub || publicBusy}
                          onClick={() => onRegisterUser(user, true)}>
                          {registeredPub ? '공용 등록됨' : publicBusy ? '등록 중...' : '공용으로 등록'}
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            /* 데스크톱 테이블 */
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>이름</th>
                    <th style={thStyle}>아이디</th>
                    <th style={thStyle}>부서</th>
                    <th style={thStyle}>현재 IP</th>
                    <th style={thStyle}>연결 상태</th>
                    <th style={thStyle}>빠른 등록</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedOnlineUsers.map((user) => {
                    const ip             = normalizeIp(user.ip_address);
                    const userKey        = `${Number(user.user_id)}|${ip}`;
                    const registeredUser = activeKeySet.has(userKey);
                    const registeredPub  = activePublicIpSet.has(ip);
                    const userBusy       = busyKey === `${Number(user.user_id)}|${ip}`;
                    const publicBusy     = busyKey === `public|${ip}`;

                    return (
                      <tr key={`${user.user_id}-${user.last_seen_at}`}>
                        <td style={tdStyle}>{user.name}</td>
                        <td style={tdStyle}>{user.login_id}</td>
                        <td style={tdStyle}>{user.department || '-'}</td>
                        <td style={tdStyle}>
                          <code style={ipCodeStyle}>{formatIpWithAlias(ip, aliasByIp.get(ip))}</code>
                        </td>
                        <td style={tdStyle}>{getStatusText(registeredUser, registeredPub)}</td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button style={registeredUser ? doneBtn : primaryBtn} disabled={registeredUser || userBusy}
                              onClick={() => onRegisterUser(user, false)}>
                              {registeredUser ? '직원 등록됨' : userBusy ? '등록 중...' : '직원으로 등록'}
                            </button>
                            {allowShared && (
                              <button style={registeredPub ? doneBtn : btn} disabled={registeredPub || publicBusy}
                                onClick={() => onRegisterUser(user, true)}>
                                {registeredPub ? '공용 등록됨' : publicBusy ? '등록 중...' : '공용으로 등록'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* 페이지네이션 */}
          <div style={paginationWrapStyle}>
            <button style={btn} disabled={onlinePage <= 1} onClick={() => onPageChange(Math.max(1, onlinePage - 1))}>이전</button>
            <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{onlinePage} / {onlineTotalPages}</span>
            <button style={btn} disabled={onlinePage >= onlineTotalPages} onClick={() => onPageChange(Math.min(onlineTotalPages, onlinePage + 1))}>다음</button>
          </div>
        </>
      )}
    </div>
  );
}
